import type { FormVariant } from "@homeinstead/analytics";
import type { ButtonVariantProps } from "@homeinstead/ui/components/Button";
import type {
  CareSeekerShallowLeadFormData,
  ShallowCareOption,
  ShallowCareOptionSelection,
} from "~www/types/forms/care-seeker-shallow-lead-form";
import type { CareOptionTuple } from "./form-options";
import type { HeroVariant } from "./hero-variants";

import { ServiceTypeTrackedClickEvent } from "@homeinstead/analytics";
import Quote from "@homeinstead/icons/Quote";
import { cn } from "@homeinstead/ui";
import { CheckboxButton } from "@homeinstead/ui/components/Checkbox";
import React, { Suspense } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Testimony } from "../Testimony";
import { testimonies, testimoniesAlternative } from "../testimonies";
import ContinueButton from "./ContinueButton";
import { CareOptions, getCareOptionLabel } from "./form-options";
import { getHeroVariantStyles } from "./hero-variants";

const getTestimony = (formVariant: FormVariant) => {
  if (["v4", "v5"].includes(formVariant)) {
    return testimoniesAlternative.step5;
  }
  if (formVariant === "v2") {
    return testimoniesAlternative.step4;
  }
  return testimonies.step4;
};

const CareOptionStep = ({
  nextStep,
  className,
  variant = "default",
  formVariant,
  helperTextUnderHeadline,
  customOptions,
  ctaVariant,
  helperText,
}: {
  nextStep: () => void;
  className?: string;
  variant?: HeroVariant;
  formVariant: FormVariant;
  helperTextUnderHeadline?: boolean;
  customOptions?: readonly CareOptionTuple[];
  ctaVariant?: ButtonVariantProps["variant"];
  helperText?: string;
}) => {
  const hasWiderLayout = ["v2", "v4", "v5"].includes(formVariant);
  const { watch, control } = useFormContext<CareSeekerShallowLeadFormData>();

  const step3Values = watch("careType");

  const { textColorClass, continueButtonVariant } =
    getHeroVariantStyles(variant);

  const selectedFieldNames = Object.entries(step3Values)
    .filter(([, value]) => value === true)
    .map(([key]) => key);

  // For V2 forms (when customOptions is provided), map field names to human-readable labels.
  // For V1 forms, keep the original field name behavior.
  const selectedCareTypeOptions = customOptions
    ? selectedFieldNames.map((fieldName) =>
        getCareOptionLabel(fieldName, customOptions),
      )
    : selectedFieldNames;

  const trackingEvent = {
    eventName: "tracked_click",
    eventPayloadGenerator: () =>
      ServiceTypeTrackedClickEvent(
        selectedCareTypeOptions as ShallowCareOptionSelection,
        formVariant,
      ),
  };

  const careTypeLeadFormStep = {
    formVariant,
    stepName: "care-type",
    userSelection: { careType: selectedCareTypeOptions },
  };

  const CareOptionsToUse = customOptions || CareOptions;

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col gap-4 gap-y-12 xl:flex-row xl:gap-y-4",
        className,
      )}
    >
      <div className="flex flex-col items-start gap-4 rounded-lg xl:col-start-1 xl:row-start-1 xl:items-start">
        <div>
          <h1
            className={cn(
              "mb-6 text-left font-bold font-jakarta",
              "text-xl md:text-1xl",
              { "mb-0": helperTextUnderHeadline },
              textColorClass,
            )}
          >
            What can we help with?
          </h1>
        </div>
        <div
          className={cn(
            "flex flex-col items-center gap-4 md:max-w-[440px] md:gap-2 xl:items-start",
            {
              "md:max-w-[560px]": hasWiderLayout,
            },
          )}
        >
          {helperTextUnderHeadline && (
            <div>
              <p className={cn("mb-4 w-full font-bold", textColorClass)}>
                {helperText ?? (
                  <>
                    If you don&apos;t see a specific type of care here, you will
                    be able to provide additional details once we match you with
                    a local office.{" "}
                  </>
                )}
              </p>
            </div>
          )}
          {CareOptionsToUse.map(
            ([fieldName, fieldLabel, checkBoxHelperText]) => {
              const checkboxPath =
                `careType.${fieldName}` as `careType.${ShallowCareOption}`;

              const isString = typeof checkBoxHelperText === "string";
              const hasCheckboxHelperText = checkBoxHelperText && isString;

              return (
                <Controller
                  control={control}
                  key={fieldName}
                  name={checkboxPath}
                  render={({ field }) => (
                    <div className="flex w-full items-center">
                      <CheckboxButton
                        buttonSize={hasCheckboxHelperText ? "large" : "short"}
                        checked={field.value as boolean}
                        containerClassName={cn("items-center md:w-full", {
                          "items-start": hasCheckboxHelperText,
                        })}
                        id={fieldName}
                        name={field.name}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                      >
                        <div className="flex flex-col items-start gap-2">
                          <span className="font-bold text-md">
                            {fieldLabel}
                          </span>
                          {hasCheckboxHelperText && (
                            <span className="font-normal text-default">
                              {checkBoxHelperText}
                            </span>
                          )}
                        </div>
                      </CheckboxButton>
                    </div>
                  )}
                />
              );
            },
          )}
          {!helperTextUnderHeadline && (
            <p className={cn("w-full py-2", textColorClass)}>
              {helperText ?? (
                <>
                  If you don&apos;t see a specific type of care here, you will
                  be able to provide additional details once we match you with a
                  local office.{" "}
                </>
              )}
            </p>
          )}
        </div>
        <div className="mt-2 w-full md:max-w-[296px]">
          <Suspense>
            <ContinueButton
              isEnabled
              leadFormStep={careTypeLeadFormStep}
              nextStep={nextStep}
              trackingEvent={trackingEvent}
              variant={ctaVariant ?? continueButtonVariant}
            />
          </Suspense>
        </div>
      </div>
      <Testimony
        className={cn(
          "mb-auto ml-auto hidden max-w-md flex-col items-end justify-end gap-4 xl:flex",
          {
            "max-w-[340px]": hasWiderLayout,
          },
        )}
        customIcon={<Quote className="h-full w-full" />}
        variant={variant}
      >
        <div className="flex flex-col gap-4 text-end">
          {getTestimony(formVariant)}
        </div>
      </Testimony>
    </div>
  );
};

export default CareOptionStep;
